import type { VariantProps } from "class-variance-authority"
import type { FC } from "react"
import { memo, useCallback, useMemo } from "react"
import {
	AvatarFallback,
	AvatarImage,
	Avatar as RadixAvatar,
} from "@radix-ui/react-avatar"
import { cva } from "class-variance-authority"
import { IconUserFill18 } from "nucleo-ui-fill-18"
import { IconCheckOutline18 as CheckIcon } from "nucleo-ui-outline-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { FeedbackStatesKeys, PainLevel } from "@productlane/db"
import type { Feedback } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import {
	getAvatarColor,
	getUserInitial,
	ImportantIcon,
	withGravatarDefault404,
} from "@productlane/ui"

import type { AvatarProps as RadixAvatarProps } from "@radix-ui/react-avatar"
import { useCompanyLogoStyles } from "./company-logo"

const baseClasses =
	"absolute -right-1 flex h-4 w-4 items-center justify-center rounded-full border-[1px] border-white text-white"

const CompleteFeedback = () => (
	<div
		className={cn("-bottom-0.5 bg-green-500 dark:border-gray-900", baseClasses)}
	>
		<CheckIcon className="h-2.5 w-2.5 flex-none" />
	</div>
)

const FeedbackStateIcon: Record<
	FeedbackStatesKeys,
	typeof CheckIcon | (() => null)
> = {
	NEW: () => null,
	COMPLETED: CompleteFeedback,
	SNOOZED: () => null,
	PROCESSED: CompleteFeedback,
	UNSNOOZED: () => null,
} as const

const variants = cva(
	[
		"inline-flex",
		"relative",
		"flex-none",
		"select-none",
		"items-center",
		"justify-center",
		"overflow-hidden",
		"rounded",
		"bg-gray-100",
		"dark:bg-gray-800",
		"cursor-default",
		"text-green-500",
		"dark:text-green-400",
	],
	{
		variants: {
			size: {
				sm: ["h-5 w-5 text-[0.6rem]"],
				md: ["h-[1.8rem] w-[1.8rem] text-xxs"],
			},
			isCompanyLogo: {
				true: "rounded",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
)

interface Props extends VariantProps<typeof variants>, RadixAvatarProps {
	image: string | undefined | null
	name: string | undefined
	colorSeed?: string | null
	noReferrer?: boolean
	brandfetchData: BrandfetchData
	linearLogoUrl?: string
	thread: Pick<
		Feedback,
		| "origin"
		| "state"
		| "lastInboundMessageAt"
		| "lastOutboundMessageAt"
		| "contactId"
	>
	painLevel?: PainLevel
}

export const UserCompanyAvatar: FC<Props> = memo(
	({
		linearLogoUrl,
		brandfetchData,
		noReferrer,
		name,
		colorSeed,
		image,
		size = "md",
		thread,
		painLevel,
	}) => {
		const initials = useMemo(() => getUserInitial(name ?? ""), [name])
		const avatarColor = useMemo(
			() => getAvatarColor(colorSeed || name || thread.contactId || ""),
			[colorSeed, name, thread.contactId],
		)

		const { style, format } = useCompanyLogoStyles(brandfetchData)
		const src = withGravatarDefault404(linearLogoUrl || format?.src || image)
		const companySrc = withGravatarDefault404(format?.src)
		const { state } = thread

		const Icon = FeedbackStateIcon[state]
		const isStateIcon = state === "PROCESSED" || state === "COMPLETED"
		const isImportant = painLevel === "HIGH"

		const IconBadge = useCallback(() => {
			if (isStateIcon) {
				return <Icon />
			}
			if (isImportant) {
				return (
					<div className="absolute -bottom-0.5 -right-1 flex h-4 w-4 items-center justify-center rounded-full border-[1px] border-white bg-red-100 backdrop-blur-sm dark:border-gray-900 dark:bg-red-950">
						<ImportantIcon className="h-[0.4rem] w-[0.4rem] text-red-500 dark:text-red-400" />
					</div>
				)
			}
			return null
		}, [Icon, isStateIcon, isImportant])

		return (
			<div className="relative flex flex-none">
				{/* Remount on src change: Radix keeps the load status per instance, so a stale
				in-flight load can flip it to "loaded" after src empties and hide the fallback. */}
				<RadixAvatar
					key={src || "initials"}
					className={cn(
						variants({
							size,
						}),
					)}
				>
					<AvatarImage
						src={src}
						referrerPolicy={noReferrer ? "no-referrer" : undefined}
						alt="Company User Image"
						className="h-full w-full overflow-visible rounded object-cover"
					/>
					<AvatarFallback>
						<RadixAvatar
							key={companySrc || "initials"}
							className={cn(
								variants({
									size,
									isCompanyLogo: true,
								}),
							)}
							style={style}
						>
							<AvatarImage
								referrerPolicy={noReferrer ? "no-referrer" : undefined}
								src={companySrc}
								className="h-full w-full object-contain"
								alt="Company Image"
							/>
							<AvatarFallback
								style={initials ? { backgroundColor: avatarColor } : undefined}
								className={cn(
									"flex h-full w-full items-center justify-center font-medium leading-[initial] text-white",
									!initials && "bg-gray-400 dark:bg-gray-600",
								)}
							>
								{initials ? (
									<div>{initials}</div>
								) : (
									<IconUserFill18
										style={{ height: "1.25em", width: "1.25em" }}
									/>
								)}
							</AvatarFallback>
						</RadixAvatar>
					</AvatarFallback>
				</RadixAvatar>
				<IconBadge />
			</div>
		)
	},
)
